import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { InputText } from './InputText';
import { SelectSingle } from './SelectSingle';
import { TextArea } from './TextArea';
import { Plus, Zap, Calendar, User, AlertCircle, X, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { api } from '../services/api';
import { enqueueSnackbar } from 'notistack';
import { GrupoAcaoDisponivel, CreateGrupoAcaoDTO, GrupoAcao } from '../types/grupos';
import { useAuth } from '../contexts/AuthContext';
import { MilitarSelectField } from './MilitarSelectField';
import { PatenteSelectField } from './PatenteSelectField';
import { OficiaisSelectField } from './OficiaisSelectField';
import { PracasSelectField } from './PracasSelectField';
import { RichTextEditor } from './RichTextEditor';
import { MilitaresMultiSelectField } from './MilitaresMultiSelectField';
import { TreinamentoSelectField } from './TreinamentoSelectField';

interface GrupoAcoesManagerProps {
  grupoId: string;
  canCreateActions: boolean;
  onActionCreated?: () => void;
}

interface CreateActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  acao: GrupoAcaoDisponivel;
  grupoId: string;
  onActionCreated?: () => void;
}

const CreateActionModal: React.FC<CreateActionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  acao,
  grupoId,
  onActionCreated
}) => {
  const { militar } = useAuth();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Resetar form data
      const initialData: Record<string, string> = {};
      acao.campos.forEach(campo => {
        initialData[campo.id] = '';
      });
      setFormData(initialData);
    }
  }, [isOpen, acao]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !militar) return;

    setIsSubmitting(true);

    try {
      const createData: CreateGrupoAcaoDTO = {
        id_militar: militar.id,
        id_grupo_tipo_acao: acao.id,
        valores_campos: acao.campos.map(campo => ({
          id_grupo_tipo_acao_campo: campo.id,
          valor: formData[campo.id] || ''
        }))
      };

      await api.post(`/grupos/${grupoId}/acoes`, createData);
      enqueueSnackbar('Ação criada com sucesso!', { variant: 'success' });
      onSave();
      onClose();
      if (onActionCreated) {
        onActionCreated();
      }
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao criar ação',
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCampo = (campo: any) => {
    const { camposTipo } = campo;
    const value = formData[campo.id] || '';

    switch (camposTipo.tipo_input) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
        return (
          <InputText
            title=""
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            placeholder={`Digite ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
            type={camposTipo.tipo_input}
          />
        );

      case 'number':
        return (
          <InputText
            title=""
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            placeholder={`Digite ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
            type="number"
            min={camposTipo.validacao?.min}
            max={camposTipo.validacao?.max}
            step={camposTipo.validacao?.step ?? 1}
          />
        );

      case 'date':
      case 'datetime-local':
        return (
          <input
            type={camposTipo.tipo_input}
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            required={camposTipo.validacao?.required}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        );

      case 'textarea':
        return (
          <TextArea
            title=""
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            placeholder={`Digite ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'select':
        return (
          <SelectSingle
            title=""
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            required={camposTipo.validacao?.required}
          >
            <option value="">Selecione uma opção...</option>
            {camposTipo.opcoes?.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            )) || []}
          </SelectSingle>
        );

      case 'militar-select':
        return (
          <MilitarSelectField
            value={value}
            onChange={(militarId, militarNick) => {
              setFormData(prev => ({ ...prev, [campo.id]: militarId }));
            }}
            placeholder={camposTipo.placeholder || `Selecione ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'patente-select':
        return (
          <PatenteSelectField
            value={value}
            onChange={(patenteId, patenteNome) => {
              setFormData(prev => ({ ...prev, [campo.id]: patenteId }));
            }}
            placeholder={camposTipo.placeholder || `Selecione ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'oficiais-select':
        return (
          <OficiaisSelectField
            value={value}
            onChange={(militarId, militarNick) => {
              setFormData(prev => ({ ...prev, [campo.id]: militarId }));
            }}
            placeholder={camposTipo.placeholder || `Selecione ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'pracas-select':
        return (
          <PracasSelectField
            value={value}
            onChange={(militarId, militarNick) => {
              setFormData(prev => ({ ...prev, [campo.id]: militarId }));
            }}
            placeholder={camposTipo.placeholder || `Selecione ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'treinamento-select':
        return (
          <TreinamentoSelectField
            value={value}
            onChange={(treinamentoId, treinamentoNome) => {
              setFormData(prev => ({ ...prev, [campo.id]: treinamentoId }));
            }}
            placeholder={camposTipo.placeholder || `Selecione ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'militares-multiselect':
        return (
          <MilitaresMultiSelectField
            value={value}
            onChange={(jsonValue) => setFormData(prev => ({ ...prev, [campo.id]: jsonValue }))}
            placeholder={camposTipo.placeholder || `Pesquise e selecione militares...`}
            required={camposTipo.validacao?.required}
          />
        );

      case 'richtext':
        return (
          <RichTextEditor
            value={value}
            onChange={(content) => setFormData(prev => ({ ...prev, [campo.id]: content }))}
            placeholder={camposTipo.placeholder || `Digite ${campo.campo.toLowerCase()}...`}
            height="250px"
            enableImages
          />
        );

      default:
        return (
          <InputText
            title=""
            name={campo.id}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [campo.id]: e.target.value }))}
            placeholder={`Digite ${campo.campo.toLowerCase()}...`}
            required={camposTipo.validacao?.required}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${isExpanded ? 'p-0' : 'p-4'}`}>
      <Card className={`w-full flex flex-col ${isExpanded ? 'h-full rounded-none' : 'max-w-2xl max-h-[90vh]'}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Criar Ação: {acao.descricao}
            </CardTitle>
            <CardDescription>
              Preencha os campos necessários para criar esta ação
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
              {acao.pontuacao_meta} pontos
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(prev => !prev)}
              className="h-6 w-6 p-0"
              title={isExpanded ? 'Restaurar' : 'Expandir'}
              disabled={isSubmitting}
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-4">
            {acao.campos.map((campo) => (
              <div key={campo.id}>
                <Label htmlFor={campo.id}>
                  {campo.campo}
                  {campo.camposTipo.validacao?.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <div className="mt-1">
                  {renderCampo(campo)}
                </div>
              </div>
            ))}

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  'Criar Ação'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export const GrupoAcoesManager: React.FC<GrupoAcoesManagerProps> = ({
  grupoId,
  canCreateActions,
  onActionCreated
}) => {
  const [acoesDisponiveis, setAcoesDisponiveis] = useState<GrupoAcaoDisponivel[]>([]);
  const [minhasAcoes, setMinhasAcoes] = useState<GrupoAcao[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAcao, setSelectedAcao] = useState<GrupoAcaoDisponivel | null>(null);

  const loadAcoesDisponiveis = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/grupos/${grupoId}/acoes/disponiveis`);
      setAcoesDisponiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar ações disponíveis:', error);
      enqueueSnackbar('Erro ao carregar ações disponíveis', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (grupoId) {
      loadAcoesDisponiveis();
    }
  }, [grupoId]);

  const handleCreateAction = (acao: GrupoAcaoDisponivel) => {
    if (!canCreateActions) {
      enqueueSnackbar('Você não tem permissão para criar ações neste grupo', { variant: 'error' });
      return;
    }
    setSelectedAcao(acao);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAcao(null);
  };

  const handleSaveSuccess = () => {
    // Recarregar ações se necessário
    loadAcoesDisponiveis();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="space-y-2">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-l-4 border-l-green-500">
        <CardHeader>
          <CardTitle className="text-green-700 dark:text-green-400 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Ações Disponíveis ({acoesDisponiveis.length})
          </CardTitle>
          <CardDescription>
            Ações que você pode criar neste grupo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {acoesDisponiveis.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>Nenhuma ação disponível no momento</p>
              <p className="text-sm mt-2">
                Entre em contato com os administradores para configurar tipos de ações
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {acoesDisponiveis.map((acao) => (
                <div
                  key={acao.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors gap-3"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center shrink-0">
                      <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground">{acao.descricao}</h3>
                      <p className="text-sm text-muted-foreground">
                        Meta: {acao.pontuacao_meta} pontos • {acao.campos.length} campo(s)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:shrink-0">
                    <Badge variant="outline" className="text-green-600 border-green-200 dark:text-green-400 dark:border-green-700">
                      {acao.pontuacao_meta}pts
                    </Badge>
                    <Button
                      size="sm"
                      onClick={() => handleCreateAction(acao)}
                      disabled={!canCreateActions}
                      className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Criar
                    </Button>
                  </div>
                </div>
              ))}

              {!canCreateActions && (
                <div className="text-center py-4">
                  <p className="text-xs text-muted-foreground">
                    Você precisa ser membro do grupo para criar ações
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedAcao && (
        <CreateActionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveSuccess}
          acao={selectedAcao}
          grupoId={grupoId}
          onActionCreated={onActionCreated}
        />
      )}
    </>
  );
};
